import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Slider } from "./ui/slider";
import { Input } from "./ui/input";
import { useApp } from "../context/AppContext";
import { ZoomIn, ZoomOut, Loader2, Type, Film } from "lucide-react";

const VIEW = 280;
const OUT = 512;
const MAX_ZOOM = 3;
const MAX_CLIP = 5; // seconds (WhatsApp-style animated sticker budget)
const COLORS = [
  { id: "white", fill: "#ffffff", stroke: "#000000" },
  { id: "yellow", fill: "#facc15", stroke: "#000000" },
  { id: "black", fill: "#111111", stroke: "#ffffff" },
  { id: "red", fill: "#ef4444", stroke: "#000000" },
];

/** Draws the caption on a square canvas (size px) — used for the live preview AND the exported overlay. */
export function drawStickerText(ctx, size, text, pos, colorId) {
  const txt = (text || "").trim();
  if (!txt) return;
  const c = COLORS.find((x) => x.id === colorId) || COLORS[0];
  const fontPx = Math.round(size * 0.115);
  ctx.save();
  ctx.font = `800 ${fontPx}px "Outfit", "Inter", system-ui, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.lineJoin = "round";
  ctx.lineWidth = Math.max(3, fontPx * 0.18);
  ctx.strokeStyle = c.stroke;
  ctx.fillStyle = c.fill;
  // word-wrap to ~88% of the width, max 3 lines
  const maxW = size * 0.88;
  const words = txt.toUpperCase().split(/\s+/);
  const lines = [];
  let cur = "";
  for (const w of words) {
    const tryLine = cur ? `${cur} ${w}` : w;
    if (ctx.measureText(tryLine).width <= maxW || !cur) cur = tryLine;
    else { lines.push(cur); cur = w; }
  }
  if (cur) lines.push(cur);
  const shown = lines.slice(0, 3);
  const lh = fontPx * 1.12;
  const total = lh * shown.length;
  const startY = pos === "top" ? size * 0.07 + fontPx : size - size * 0.07 - total + fontPx;
  shown.forEach((ln, i) => {
    const y = startY + i * lh;
    ctx.strokeText(ln, size / 2, y, maxW);
    ctx.fillText(ln, size / 2, y, maxW);
  });
  ctx.restore();
}

/**
 * Sticker Studio: photo / GIF / short video → crop (drag + zoom) + caption → sticker.
 * Static photos are rendered client-side (PNG); GIF/video go to the server (animated WebP).
 */
export function StickerStudio({ open, file, onClose, onCreateStatic, onCreateAnimated }) {
  const { t } = useApp();
  const kind = useMemo(() => {
    if (!file) return "image";
    if (/^video\//.test(file.type) || /\.(mp4|mov|m4v|webm)$/i.test(file.name)) return "video";
    if (file.type === "image/gif" || /\.gif$/i.test(file.name)) return "gif";
    return "image";
  }, [file]);
  const animated = kind !== "image";
  const [srcUrl, setSrcUrl] = useState(null);
  const mediaRef = useRef(null);
  const [nat, setNat] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState("");
  const [textPos, setTextPos] = useState("bottom");
  const [color, setColor] = useState("white");
  const [vidDur, setVidDur] = useState(0);
  const [clipStart, setClipStart] = useState(0);
  const [noPreview, setNoPreview] = useState(false); // browser can't decode this clip → server crops the center square
  const clipLen = Math.max(0.3, Math.min(MAX_CLIP, vidDur - clipStart));

  const baseScale = nat ? VIEW / Math.min(nat.w, nat.h) : 1;
  const scale = baseScale * zoom;

  useEffect(() => {
    if (!open || !file) return undefined;
    const url = URL.createObjectURL(file);
    setSrcUrl(url); setZoom(1); setText(""); setTextPos("bottom"); setColor("white"); setClipStart(0); setVidDur(0); setNat(null); setNoPreview(false);
    return () => { URL.revokeObjectURL(url); setSrcUrl(null); mediaRef.current = null; };
  }, [open, file]);

  const onMediaError = () => { if (kind === "video") setNoPreview(true); else onClose?.(); };

  const onLoaded = (e) => {
    const el = e.currentTarget;
    const w = el.naturalWidth || el.videoWidth, h = el.naturalHeight || el.videoHeight;
    if (!w || !h) return;
    mediaRef.current = el;
    const b = VIEW / Math.min(w, h);
    setNat({ w, h });
    setPos({ x: (VIEW - w * b) / 2, y: (VIEW - h * b) / 2 });
    if (kind === "video" && isFinite(el.duration)) setVidDur(el.duration);
  };

  // Loop the chosen clip in the preview
  useEffect(() => {
    const v = mediaRef.current;
    if (kind !== "video" || !v || !vidDur) return undefined;
    const onTime = () => { if (v.currentTime > clipStart + clipLen || v.currentTime < clipStart - 0.05) v.currentTime = clipStart; };
    v.currentTime = clipStart;
    v.addEventListener("timeupdate", onTime);
    v.play().catch(() => {});
    return () => v.removeEventListener("timeupdate", onTime);
  }, [kind, vidDur, clipStart, clipLen]);

  const clampPos = (x, y, s) => ({ x: Math.min(0, Math.max(VIEW - (nat?.w || 0) * s, x)), y: Math.min(0, Math.max(VIEW - (nat?.h || 0) * s, y)) });
  const applyZoom = (z) => {
    if (!nat) return;
    const s0 = baseScale * zoom, s1 = baseScale * z;
    const cx = (VIEW / 2 - pos.x) / s0, cy = (VIEW / 2 - pos.y) / s0;
    setPos(clampPos(VIEW / 2 - cx * s1, VIEW / 2 - cy * s1, s1));
    setZoom(z);
  };
  const onPointerDown = (e) => { e.preventDefault(); dragRef.current = { sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y }; try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ } };
  const onPointerMove = (e) => { if (!dragRef.current || !nat) return; setPos(clampPos(dragRef.current.ox + (e.clientX - dragRef.current.sx), dragRef.current.oy + (e.clientY - dragRef.current.sy), scale)); };
  const endDrag = () => { dragRef.current = null; };

  // Live caption preview (same renderer as the export → WYSIWYG)
  const overlayUrl = useMemo(() => {
    if (!text.trim()) return null;
    const c = document.createElement("canvas"); c.width = OUT; c.height = OUT;
    drawStickerText(c.getContext("2d"), OUT, text, textPos, color);
    return c.toDataURL("image/png");
  }, [text, textPos, color]);

  const confirm = async () => {
    const el = mediaRef.current;
    if (noPreview && animated) {
      setBusy(true);
      try {
        const fd = new FormData();
        fd.append("file", file, file.name || "clip.mp4");
        fd.append("crop_w", "0"); fd.append("crop_h", "0"); // center square on the server
        fd.append("start", "0"); fd.append("duration", String(MAX_CLIP));
        if (overlayUrl) fd.append("overlay_base64", overlayUrl.split(",")[1]);
        await onCreateAnimated(fd);
      } finally { setBusy(false); }
      return;
    }
    if (!el || !nat) return;
    setBusy(true);
    try {
      const sx = -pos.x / scale, sy = -pos.y / scale, sSize = VIEW / scale;
      if (!animated) {
        const canvas = document.createElement("canvas"); canvas.width = OUT; canvas.height = OUT;
        const ctx = canvas.getContext("2d");
        const r = OUT * 0.12;
        ctx.beginPath(); ctx.moveTo(r, 0); ctx.arcTo(OUT, 0, OUT, OUT, r); ctx.arcTo(OUT, OUT, 0, OUT, r); ctx.arcTo(0, OUT, 0, 0, r); ctx.arcTo(0, 0, OUT, 0, r); ctx.closePath(); ctx.clip();
        ctx.drawImage(el, sx, sy, sSize, sSize, 0, 0, OUT, OUT);
        drawStickerText(ctx, OUT, text, textPos, color);
        await onCreateStatic(canvas.toDataURL("image/png").split(",")[1]);
      } else {
        const fd = new FormData();
        fd.append("file", file, file.name || (kind === "gif" ? "sticker.gif" : "clip.mp4"));
        fd.append("crop_x", String(sx / nat.w)); fd.append("crop_y", String(sy / nat.h));
        fd.append("crop_w", String(sSize / nat.w)); fd.append("crop_h", String(sSize / nat.h));
        fd.append("start", String(kind === "video" ? clipStart : 0));
        fd.append("duration", String(kind === "video" ? clipLen : MAX_CLIP));
        if (overlayUrl) fd.append("overlay_base64", overlayUrl.split(",")[1]);
        await onCreateAnimated(fd);
      }
    } finally { setBusy(false); }
  };

  const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose?.()}>
      <DialogContent className="max-w-sm max-h-[92dvh] overflow-y-auto" data-testid="sticker-studio">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">{t("sticker_studio")} {animated && <Film className="h-4 w-4 text-primary" />}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center gap-3">
          <div
            className="relative overflow-hidden rounded-xl bg-neutral-950 select-none cursor-grab active:cursor-grabbing"
            style={{ width: VIEW, height: VIEW, touchAction: "none" }}
            onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={endDrag} onPointerCancel={endDrag}
            data-testid="sticker-studio-viewport"
          >
            {srcUrl && kind === "video" ? (
              <video ref={mediaRef} src={srcUrl} muted loop playsInline autoPlay onLoadedMetadata={onLoaded} onError={onMediaError} draggable={false}
                className="absolute max-w-none pointer-events-none" style={nat ? { left: pos.x, top: pos.y, width: nat.w * scale, height: nat.h * scale } : { opacity: 0 }} />
            ) : srcUrl ? (
              <img src={srcUrl} alt="" onLoad={onLoaded} onError={onMediaError} draggable={false}
                className="absolute max-w-none pointer-events-none" style={nat ? { left: pos.x, top: pos.y, width: nat.w * scale, height: nat.h * scale } : { opacity: 0 }} />
            ) : null}
            {noPreview && (
              <div className="absolute inset-0 flex items-center justify-center p-6 text-center text-xs text-white/80" data-testid="sticker-studio-no-preview">
                {t("sticker_no_preview")}
              </div>
            )}
            {overlayUrl && <img src={overlayUrl} alt="" className="absolute inset-0 w-full h-full pointer-events-none" data-testid="sticker-studio-text-preview" />}
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute inset-0 border-2 border-white/90 rounded-[12%]" style={{ boxShadow: "0 0 0 9999px rgba(0,0,0,0.6)" }} />
            </div>
          </div>
          <div className="flex items-center gap-3 w-full px-1">
            <ZoomOut className="h-4 w-4 text-muted-foreground shrink-0" />
            <Slider value={[zoom]} min={1} max={MAX_ZOOM} step={0.01} onValueChange={([z]) => applyZoom(z)} className="flex-1" data-testid="sticker-studio-zoom" />
            <ZoomIn className="h-4 w-4 text-muted-foreground shrink-0" />
          </div>
          {kind === "video" && vidDur > MAX_CLIP + 0.2 && (
            <div className="w-full px-1" data-testid="sticker-studio-clip">
              <div className="flex justify-between text-[11px] text-muted-foreground mb-1">
                <span>{t("sticker_clip_start")}</span>
                <span className="tabular-nums">{fmt(clipStart)} – {fmt(clipStart + clipLen)} · {clipLen.toFixed(1)}s</span>
              </div>
              <Slider value={[clipStart]} min={0} max={Math.max(0, vidDur - 0.5)} step={0.1} onValueChange={([s]) => setClipStart(s)} data-testid="sticker-studio-clip-start" />
            </div>
          )}
          <div className="w-full space-y-2" data-testid="sticker-studio-text">
            <div className="flex items-center gap-2">
              <Type className="h-4 w-4 text-muted-foreground shrink-0" />
              <Input value={text} onChange={(e) => setText(e.target.value.slice(0, 60))} placeholder={t("sticker_text_placeholder")} className="h-10" data-testid="sticker-studio-text-input" />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex rounded-lg border overflow-hidden text-xs">
                {["top", "bottom"].map((p) => (
                  <button key={p} type="button" onClick={() => setTextPos(p)} data-testid={`sticker-studio-text-${p}`}
                    className={`px-3 h-8 ${textPos === p ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>{t(p === "top" ? "text_top" : "text_bottom")}</button>
                ))}
              </div>
              <div className="flex items-center gap-1.5 ml-auto">
                {COLORS.map((c) => (
                  <button key={c.id} type="button" onClick={() => setColor(c.id)} aria-label={c.id} data-testid={`sticker-studio-color-${c.id}`}
                    className={`h-7 w-7 rounded-full border-2 ${color === c.id ? "border-primary scale-110" : "border-border"} transition-transform`} style={{ backgroundColor: c.fill }} />
                ))}
              </div>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground text-center px-2">{t(animated ? "sticker_studio_hint_anim" : "crop_hint_sticker")}</p>
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="sticker-studio-cancel">{t("cancel")}</Button>
          <Button onClick={confirm} disabled={busy || (!nat && !noPreview)} data-testid="sticker-studio-confirm">
            {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t("sticker_creating")}</> : t("create_sticker")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
